import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  DataTable,
  EmptyState,
  Input,
  Modal,
  Pagination,
  Select,
  SegmentedControl,
  Skeleton,
  StatusPill,
  Switch,
  TruncatedText,
  type DataTableColumn,
  useCursorPagination,
} from "@playbookai/ui";
import type {
  EffectiveIntegrationTool,
  IntegrationConnectionDetail,
  PlayerAiToolAccessMode,
} from "@playbookai/api-clients";
import {
  SETTINGS_CONNECTIONS_ROUTE,
  SETTINGS_PLAYER_AI_CONNECTED_APPS_ROUTE,
} from "../../routes.js";
import { useGuardedNavigate } from "../../settings/leave-guard.js";
import {
  useConnectionToolsApi,
  usePagedConnectionTools,
} from "./use-connection-tools.js";
import {
  ConnectionStatus,
  connectionIdentity,
} from "./PlayerAIConnectedApps.js";
import { notAvailableCopy } from "./tool-denials.js";

export function ConnectionToolsSettings() {
  const { connectionId = "" } = useParams();
  const { api, scope } = useConnectionToolsApi();
  const connection = useQuery({
    queryKey: [...scope, "connection", connectionId],
    queryFn: () => api.get(connectionId),
    retry: false,
  });
  const integrations = useQuery({
    queryKey: [...scope, "integrations"],
    queryFn: () => api.listConnectable(),
    retry: false,
  });
  const [params, setParams] = useSearchParams();
  const search = params.get("q") ?? "";
  const pageSize = params.get("pageSize") === "50" ? 50 : 25;
  const paging = useCursorPagination(`${connectionId}|${search}|${pageSize}`, {
    pageSize,
  });
  const tools = usePagedConnectionTools(connectionId, {
    ...(paging.cursor ? { cursor: paging.cursor } : {}),
    limit: pageSize,
    ...(search ? { query: search } : {}),
  });
  paging.reportTotal(tools.query.data?.totalCount);
  const queryClient = useQueryClient();
  const mode = useMutation({
    mutationFn: (next: PlayerAiToolAccessMode) =>
      api.setPlayerAiToolAccessMode(connectionId, next),
    onSuccess: async ({ mode: savedMode }) => {
      queryClient.setQueryData<IntegrationConnectionDetail>(
        [...scope, "connection", connectionId],
        (current) =>
          current ? { ...current, playerAiToolAccessMode: savedMode } : current,
      );
      await tools.query.refetch();
    },
  });
  const preference = useMutation({
    mutationFn: ({ entryId, enabled }: { entryId: string; enabled: boolean }) =>
      api.setPlayerAiToolPreference(connectionId, entryId, enabled),
    onSuccess: async () => {
      await tools.query.refetch();
    },
  });
  const navigate = useGuardedNavigate();
  const backSearch = params.get("search");

  return (
    <div className="space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={() =>
          navigate(
            `${SETTINGS_PLAYER_AI_CONNECTED_APPS_ROUTE}${backSearch ? `?search=${encodeURIComponent(backSearch)}` : ""}`,
          )
        }
      >
        ← Back to Connected apps
      </Button>
      {connection.isPending ? <Skeleton className="h-20" /> : null}
      {connection.isError ? (
        <Alert tone="critical" title="Connection unavailable">
          It may have been removed or your access changed.{" "}
          <Button size="sm" onClick={() => void connection.refetch()}>
            Retry
          </Button>
        </Alert>
      ) : null}
      {connection.data ? (
        <section className="border-b border-pb-border pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-heading-3 text-pb-text">
              {connection.data.displayName}
            </h2>
            <ConnectionStatus connection={connection.data} />
          </div>
          <p className="mt-1 text-body-small text-pb-text-secondary">
            {connectionIdentity(
              connection.data,
              integrations.data?.find(
                (integration) =>
                  integration.id === connection.data.tenantIntegrationId,
              ),
            )}
          </p>
          {connection.data.description ? (
            <p className="mt-3 break-words text-body-small text-pb-text-secondary">
              {connection.data.description}
            </p>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(SETTINGS_CONNECTIONS_ROUTE)}
          >
            Connection settings
          </Button>
        </section>
      ) : null}
      {/* Kept hand-built: SectionHeader spaces the title 4px above a
          max-w-xl text-sm line and adds mb-6, a look change no PR 3 row
          approves. */}
      <div>
        <h2 className="text-heading-3 text-pb-text">Tools for Player AI</h2>
        <p className="mt-1 text-body-small text-pb-text-secondary">
          Start with your workspace policy, use read-only access, or choose
          individual tools. Workspace restrictions always apply.
        </p>
      </div>
      {connection.data && tools.query.data ? (
        <PlayerAiAccessModeCard
          mode={connection.data.playerAiToolAccessMode}
          pending={mode.isPending}
          onChange={(next) => mode.mutate(next)}
        />
      ) : null}
      {mode.isError ? (
        <Alert tone="critical" title="Could not update tool access">
          Your previous setting is still active. Try again.
        </Alert>
      ) : null}
      {preference.isError ? (
        <Alert tone="critical" title="Could not update this tool">
          Reloaded workspace policy may have changed. Try again.
        </Alert>
      ) : null}
      {tools.query.isPending ? <Skeleton className="h-40" /> : null}
      {tools.query.isError ? (
        <Alert tone="critical" title="Tool access unavailable">
          Check the connection and workspace access.{" "}
          <Button size="sm" onClick={() => void tools.query.refetch()}>
            Retry
          </Button>
        </Alert>
      ) : null}
      {tools.query.data && connection.data ? (
        <ToolsTable
          tools={tools.query.data.data}
          mode={connection.data.playerAiToolAccessMode}
          search={search}
          onSearchChange={(value) => {
            const next = new URLSearchParams(params);
            value ? next.set("q", value) : next.delete("q");
            setParams(next, { replace: true });
          }}
          pageSize={pageSize}
          onPageSizeChange={(value) => {
            const next = new URLSearchParams(params);
            value === 25
              ? next.delete("pageSize")
              : next.set("pageSize", String(value));
            setParams(next, { replace: true });
          }}
          page={paging.page}
          {...(paging.totalCount === undefined
            ? {}
            : { totalCount: paging.totalCount })}
          canGoBack={paging.canGoBack}
          hasNext={tools.query.data.hasMore}
          busy={tools.query.isFetching}
          onPrev={paging.goBack}
          onNext={() => {
            const nextCursor = tools.query.data?.nextCursor;
            if (nextCursor) paging.goNext(nextCursor);
          }}
          {...(preference.isPending && preference.variables?.entryId
            ? { pendingEntryId: preference.variables.entryId }
            : {})}
          onToggle={(entryId, enabled) =>
            preference.mutate({ entryId, enabled })
          }
        />
      ) : null}
    </div>
  );
}

function PlayerAiAccessModeCard({
  mode,
  pending,
  onChange,
}: {
  readonly mode: PlayerAiToolAccessMode;
  readonly pending: boolean;
  readonly onChange: (mode: PlayerAiToolAccessMode) => void;
}) {
  return (
    <Card aria-label="Player AI tool access">
      <CardBody className="space-y-3">
        <div>
          <h3 className="text-sm font-medium text-pb-text">Tool access</h3>
          <p className="mt-1 text-dense-1 text-pb-text-muted">
            New connections follow workspace defaults. You can choose a
            different policy for this connection at any time.
          </p>
        </div>
        <SegmentedControl<PlayerAiToolAccessMode>
          aria-label="Player AI tool access mode"
          value={mode}
          options={[
            {
              value: "workspace_default",
              label: "Workspace default",
              disabled: pending,
            },
            {
              value: "read_only",
              label: "Read only",
              disabled: pending,
            },
            { value: "custom", label: "Custom", disabled: pending },
          ]}
          onChange={onChange}
        />
        <p className="text-caption text-pb-text-muted">
          {mode === "workspace_default"
            ? "Follows changes your admin makes."
            : mode === "read_only"
              ? "Player AI can read information but cannot create or update it."
              : "Choose the tools Player AI may use below."}
        </p>
      </CardBody>
    </Card>
  );
}

export function ToolsTable({
  tools,
  mode,
  pendingEntryId,
  search,
  onSearchChange,
  pageSize,
  onPageSizeChange,
  page,
  totalCount,
  canGoBack,
  hasNext,
  busy,
  onPrev,
  onNext,
  onToggle,
}: {
  readonly tools: readonly EffectiveIntegrationTool[];
  readonly mode: PlayerAiToolAccessMode;
  readonly pendingEntryId?: string;
  readonly search: string;
  readonly onSearchChange: (value: string) => void;
  readonly pageSize: 25 | 50;
  readonly onPageSizeChange: (value: 25 | 50) => void;
  readonly page: number;
  readonly totalCount?: number;
  readonly canGoBack: boolean;
  readonly hasNext: boolean;
  readonly busy: boolean;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  readonly onToggle: (entryId: string, enabled: boolean) => void;
}) {
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState<EffectiveIntegrationTool | null>(null);
  const available = tools.filter((tool) => tool.playerAiInEffect).length;
  const filtered = tools.filter(
    (tool) =>
      `${tool.title ?? ""} ${tool.toolName} ${tool.description ?? ""}`
        .toLocaleLowerCase()
        .includes(search.toLocaleLowerCase()) &&
      (filter === "all" ||
        (filter === "available"
          ? tool.playerAiInEffect
          : !tool.playerAiInEffect)),
  );
  const pageTools = filtered;
  const columns: ReadonlyArray<DataTableColumn<EffectiveIntegrationTool>> = [
    {
      id: "tool",
      header: "Tool",
      width: "60%",
      cardRole: "title",
      cell: (tool) => (
        <div className="min-w-0">
          {/* Kept restyled: Button link tone="neutral" size="inline" is
              underlined and inherits the weight, and plain drops the ghost
              hover paint; no PR 3 look row approves either. */}
          <Button
            variant="ghost"
            className="h-auto max-w-full justify-start px-0 py-0 text-left font-medium text-pb-text"
            onClick={() => setDetail(tool)}
          >
            <span className="truncate">{tool.title || tool.toolName}</span>
          </Button>
          {tool.description ? (
            /* A clipped description shows in the design-system Tooltip
               (d1), not a native title. */
            <p className="max-w-md text-caption text-pb-text-muted">
              <TruncatedText>{tool.description}</TruncatedText>
            </p>
          ) : null}
        </div>
      ),
    },
    {
      id: "access",
      header: "Access",
      cell: (tool) =>
        tool.riskClass === "write"
          ? "Write"
          : tool.riskClass === "read"
            ? "Read"
            : "Unclassified",
    },
    {
      id: "availability",
      header: mode === "custom" ? "Player AI" : "Available",
      align: "end",
      cardRole: "actions",
      cell: (tool) =>
        mode === "custom" && tool.manualInEffect ? (
          <Switch
            checked={tool.playerAiSelected}
            disabled={pendingEntryId !== undefined}
            aria-label={`Allow ${tool.title || tool.toolName} for Player AI`}
            onChange={(enabled) => onToggle(tool.id, enabled)}
          />
        ) : (
          <div className="flex flex-col items-end gap-1">
            <StatusPill tone={tool.playerAiInEffect ? "healthy" : "neutral"}>
              {tool.playerAiInEffect ? "Available" : "Unavailable"}
            </StatusPill>
            {!tool.playerAiInEffect && tool.playerAiBlockedBy.length > 0 ? (
              <span className="max-w-64 text-right text-caption text-pb-text-muted">
                {notAvailableCopy(tool.playerAiBlockedBy)}
              </span>
            ) : null}
          </div>
        ),
    },
  ];

  return (
    <div className="space-y-3">
      <Input
        type="search"
        aria-label="Search tools"
        placeholder="Search tools"
        value={search}
        onChange={(event) => {
          onSearchChange(event.target.value);
        }}
      />
      <SegmentedControl
        aria-label="Tool access filter"
        value={filter}
        options={[
          { value: "all", label: `All (${tools.length})` },
          { value: "available", label: `Available (${available})` },
          {
            value: "unavailable",
            label: `Unavailable (${tools.length - available})`,
          },
        ]}
        onChange={(value) => {
          setFilter(value);
        }}
      />
      <p className="text-caption text-pb-text-muted">
        {available} of {tools.length} tools on this page are available.
      </p>
      {filtered.length === 0 ? (
        <EmptyState
          title={tools.length ? "No matching tools" : "No tools available"}
          description={
            tools.length
              ? "Try another search or access filter."
              : "Your admin can sync and enable tools in this integration."
          }
        />
      ) : (
        <DataTable
          layout="fixed"
          columns={columns}
          data={pageTools}
          getRowId={(tool) => tool.id}
          caption="Tools available through this connection"
          textSize="small"
          density="compact"
          responsive
          pinFirstColumn
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-caption text-pb-text-muted">
          {filtered.length} tools on this page
        </p>
        <Select
          ariaLabel="Tools per page"
          value={String(pageSize)}
          options={[25, 50].map((size) => ({
            value: String(size),
            label: `${size} per page`,
          }))}
          onChange={(value) => onPageSizeChange(value === "50" ? 50 : 25)}
          className="w-36"
        />
        {canGoBack || hasNext ? (
          <Pagination
            mode="cursor"
            page={page}
            pageItemCount={tools.length}
            totalCount={totalCount}
            canGoBack={canGoBack}
            hasNext={hasNext}
            busy={busy}
            onPrev={onPrev}
            onNext={onNext}
          />
        ) : null}
      </div>
      <Modal
        open={detail !== null}
        title={detail?.title || detail?.toolName || "Tool details"}
        onClose={() => setDetail(null)}
        size="md"
        footer={
          <Button variant="secondary" onClick={() => setDetail(null)}>
            Close
          </Button>
        }
      >
        <p className="whitespace-pre-wrap break-words text-body-small text-pb-text-secondary">
          {detail?.description || "No description provided."}
        </p>
      </Modal>
    </div>
  );
}
